import React, { SyntheticEvent } from 'react';
import moment from 'moment-timezone';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { getHoursRangeText } from '@app/i18n';
import { Session, SessionFormat, Participation } from '@app/api';
import { getFormattedPhoneNumber } from 'lib/participantUtils';

import {
  Link,
  MenuItem,
  DotsMenu,
  FormatIcon,
  TextLoadingPlaceholder,
  useParticipationDisplayName,
} from 'components';

const useStyles = createUseStyles((theme: Theme) => ({
  line: {
    width: '100%',
    padding: `${theme.marginBase}px ${theme.marginBase * 2}px`,
    paddingLeft: 0,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.colors.white,

    marginBottom: theme.marginBase,
    borderRadius: theme.borderRadius.std,

    minHeight: theme.marginBase * 8,

    flexWrap: 'wrap',
    position: 'relative',

    [theme.media.tablet]: {
      paddingRight: theme.marginBase * 7,
    },
  },
  lineDisabled: {
    opacity: 0.5,
  },
  lineItem: {
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase,
    marginLeft: theme.marginBase * 3,
  },
  formatContainer: {
    width: theme.marginBase * 3,
  },
  hours: {
    width: 120,
    ...theme.fonts.label,
  },
  participantName: {
    ...theme.fonts.caption,
    maxWidth: 240,
    flex: 1,
    minWidth: 120,
    [theme.media.tablet]: {
      maxWidth: '100%',
    },
    [theme.media.mobile]: {
      width: '100%',
      flex: 'auto',
    },
  },
  email: {
    ...theme.fonts.caption,
    flex: 1,
    [theme.media.tablet]: {
      flex: 'auto',
    },
    [theme.media.mobile]: {
      width: '100%',
    },
  },
  phoneNumber: {
    ...theme.fonts.caption,
    width: 120,
    [theme.media.tablet]: {
      width: 'auto',
      maxWidth: '100%',
      minWidth: 120,
    },
  },
  participantAddress: {
    maxWidth: 180,
    flex: 1,
  },
  link: {
    ...theme.fonts.caption,
    color: theme.colors.blueAccessible,
  },
  address: {
    marginTop: theme.marginBase * 2,
    whiteSpace: 'pre-wrap',
  },
  menu: {
    maxWidth: 40,
    minWidth: 40,
    [theme.media.tablet]: {
      position: 'absolute',
      top: theme.marginBase,
      right: theme.marginBase,
    },
  },
  clickable: {
    '&:hover': {
      boxShadow: theme.boxShadow.base,
    },
    cursor: 'pointer',
  },
}));

interface Props {
  date: Date;
  session: Session;
  onCancelRequest: (id: string) => any;
  onClick: (participation?: Participation) => void;
  onAddressClick: (participantAdress?: string) => void;
  participation?: Participation;
}

export const AppointmentListItem = ({
  date,
  session,
  onCancelRequest,
  onClick,
  onAddressClick,
  participation,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t, i18n } = useTranslation();

  const disabled = !participation;

  const onCancel = () => participation && onCancelRequest(participation.id);
  const participant = participation && participation.participant;

  const _onClick = () => onClick(participation);

  const name = useParticipationDisplayName(participation, true);

  return (
    <button
      className={classnames(classes.line, {
        [classes.lineDisabled]: disabled,
        [classes.clickable]: !disabled,
      })}
      disabled={disabled}
      type="button"
      onClick={_onClick}
    >
      <div className={classnames(classes.lineItem, classes.formatContainer)}>
        {participation && participation.format && (
          <FormatIcon format={participation.format} />
        )}
      </div>
      <div className={classnames(classes.lineItem, classes.hours)}>
        {getHoursRangeText(
          date,
          participation?.duration ? participation.duration : session.duration,
          i18n.language,
        )}
      </div>
      {participation && (
        <React.Fragment>
          <div
            className={classnames(classes.lineItem, classes.participantName)}
          >
            {name ? (
              name
            ) : (
              <TextLoadingPlaceholder sample="Mme Alexandra Raymond" />
            )}
          </div>
          <div className={classnames(classes.lineItem, classes.email)}>
            {participant ? (
              participant.email
            ) : (
              <TextLoadingPlaceholder sample="alexandra.raymond@gmail.com" />
            )}
          </div>
          <div className={classnames(classes.lineItem, classes.phoneNumber)}>
            {participant ? (
              getFormattedPhoneNumber(participant.phoneNumber)
            ) : (
              <TextLoadingPlaceholder sample="09 87 65 43 21" />
            )}
          </div>
          <div
            className={classnames(classes.lineItem, classes.participantAddress)}
          >
            {participation.format === SessionFormat.IN_PARTICIPANT_PLACE &&
              participation.participantAddress && (
                <Link
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    onAddressClick(participation.participantAddress);
                  }}
                  className={classes.link}
                >
                  <Trans i18nKey="sessions.details.participantAddress" />
                </Link>
              )}
          </div>
          <div className={classnames(classes.lineItem, classes.menu)}>
            {!disabled && moment(date).isAfter(moment()) && (
              <DotsMenu>
                <MenuItem
                  label={t('sessions.details.menu.cancel')}
                  onClick={onCancel}
                />
              </DotsMenu>
            )}
          </div>
        </React.Fragment>
      )}
    </button>
  );
};
